import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  Stack,
  Card,
  CardBody,
  CardHeader,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Flex,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  IconButton,
  HStack,
  VStack,
  Spinner,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Image,
} from '@chakra-ui/react';
import {
  ViewIcon,
  EditIcon,
  StarIcon,
  AddIcon,
  DeleteIcon,
} from '@chakra-ui/icons';
import { resolveProductImageUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../contexts/ProductContext';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types/product';

const AgroDealerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { updateProduct, deleteProduct, deactivateProduct, getProductsByFarmer } = useProducts();
  const [myProducts, setMyProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editImageFiles, setEditImageFiles] = useState<File[]>([]);
  const [editForm, setEditForm] = useState({
    name: '',
    category: '',
    price: '',
    quantity: '',
    unit: '',
    brand: '',
    condition: '',
    description: '',
  });

  useEffect(() => {
    const loadMyProducts = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const products = await getProductsByFarmer(user.id);
        setMyProducts(products);
      } catch (error) {
        console.error('Error loading products:', error);
        toast({
          title: 'Error loading inventory',
          description: 'Please try refreshing the page',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadMyProducts();
  }, [user, getProductsByFarmer, toast]);

  const quickActions = [
    {
      title: 'List Supply / Equipment',
      icon: AddIcon,
      action: () => navigate('/dashboard/add-product'),
      description: 'Post seeds, fertilizers, generators, pumps, or tools for sale',
    },
    {
      title: 'View Marketplace',
      icon: ViewIcon,
      action: () => navigate('/marketplace'),
      description: 'Browse all products listed on the platform',
    },
  ];

  const handleEditProduct = (product: Product) => {
    if (!product._id) return;
    setEditingProduct(product);
    setEditImageFiles([]);
    setEditForm({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      quantity: product.quantity.toString(),
      unit: product.unit,
      brand: product.brand || '',
      condition: product.condition || 'new',
      description: product.description,
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingProduct || !editingProduct._id) return;

    try {
      const formData = new FormData();
      formData.append('name', editForm.name);
      formData.append('category', editForm.category);
      formData.append('price', String(parseFloat(editForm.price)));
      formData.append('quantity', String(parseInt(editForm.quantity)));
      formData.append('unit', editForm.unit);
      formData.append('description', editForm.description);
      if (editForm.brand) formData.append('brand', editForm.brand);
      if (editForm.condition) formData.append('condition', editForm.condition);

      editImageFiles.forEach((file) => formData.append('images', file));

      await updateProduct(editingProduct._id, formData);

      const refreshed = await getProductsByFarmer(user!.id);
      setMyProducts(refreshed);

      toast({
        title: 'Item Updated',
        description: 'Your inventory item has been successfully updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating item:', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update item. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this listing?')) {
      try {
        await deleteProduct(productId);
        setMyProducts((prev) => prev.filter((p) => p._id !== productId));
        toast({
          title: 'Listing Deleted',
          description: 'Product removed from your catalog.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        toast({
          title: 'Delete Failed',
          description: error.message || 'Failed to delete listing.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleDeactivateProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to deactivate this item?')) {
      try {
        await deactivateProduct(productId);
        setMyProducts((prev) =>
          prev.map((p) => (p._id === productId ? { ...p, status: 'deactivated' } : p))
        );
        toast({
          title: 'Item Deactivated',
          description: 'Product deactivated and hidden from public search.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        toast({
          title: 'Deactivation Failed',
          description: error.message || 'Failed to deactivate item.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      {/* Welcome Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <HStack spacing={3} mb={1}>
            <Heading size="lg">Welcome, {user?.fullName}</Heading>
            <Badge colorScheme="purple" fontSize="0.9em" px={3} py={1} borderRadius="full">
              Agro Dealer / Vendor
            </Badge>
          </HStack>
          <Text color="gray.600">
            Manage your agricultural supplies, machinery, equipment, and shop listings
          </Text>
        </Box>
        <Button
          colorScheme="purple"
          size="lg"
          leftIcon={<AddIcon />}
          onClick={() => navigate('/dashboard/add-product')}
        >
          List Supply / Equipment
        </Button>
      </Flex>

      <Grid templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
        {/* Main Stats Section */}
        <GridItem colSpan={{ base: 1, lg: 2 }}>
          <Stack spacing={6}>
            {/* Shop Overview */}
            <Card>
              <CardHeader pb={2}>
                <Heading size="md">Shop & Business Overview</Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <Stat>
                    <StatLabel>Location / City</StatLabel>
                    <StatNumber fontSize="xl">{user?.farmLocation || 'Not specified'}</StatNumber>
                    <StatHelpText>Business Hub</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>Active Listings</StatLabel>
                    <StatNumber fontSize="xl">
                      {myProducts.filter((p) => p.status !== 'deactivated').length} items
                    </StatNumber>
                    <StatHelpText>Listed in catalog</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>Contact Phone</StatLabel>
                    <StatNumber fontSize="xl">{user?.phoneNumber || 'N/A'}</StatNumber>
                    <StatHelpText>Buyer Helpline</StatHelpText>
                  </Stat>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader pb={2}>
                <Heading size="md">Quick Actions</Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {quickActions.map((action, index) => (
                    <Button
                      key={index}
                      leftIcon={<action.icon />}
                      onClick={action.action}
                      variant="outline"
                      colorScheme="purple"
                      justifyContent="flex-start"
                      height="auto"
                      py={4}
                      px={6}
                    >
                      <Box textAlign="left">
                        <Text fontWeight="bold">{action.title}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {action.description}
                        </Text>
                      </Box>
                    </Button>
                  ))}
                </SimpleGrid>
              </CardBody>
            </Card>
          </Stack>
        </GridItem>

        {/* Inventory Sidebar */}
        <GridItem colSpan={1}>
          <Card>
            <CardHeader pb={0}>
              <Heading size="md">My Inventory & Supplies</Heading>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <Box textAlign="center" py={4}>
                  <Spinner size="lg" color="purple.500" />
                </Box>
              ) : (
                <Tabs colorScheme="purple" isFitted variant="enclosed">
                  <TabList mb={4}>
                    <Tab fontSize="sm" fontWeight="600">
                      Active ({myProducts.filter((p) => p.status !== 'deactivated').length})
                    </Tab>
                    <Tab fontSize="sm" fontWeight="600">
                      Deactivated ({myProducts.filter((p) => p.status === 'deactivated').length})
                    </Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0} py={2}>
                      <Stack spacing={4}>
                        {myProducts
                          .filter((p) => p.status !== 'deactivated')
                          .map((product) => (
                            <Box key={product._id || product.id} p={3} borderWidth="1px" borderRadius="md">
                              <Flex justify="space-between" align="start">
                                <Box>
                                  <HStack spacing={2} align="center" mb={1}>
                                    <Heading size="sm">{product.name}</Heading>
                                    <Badge colorScheme="purple">{product.category}</Badge>
                                  </HStack>
                                  <Text fontSize="sm" color="gray.600">
                                    Price: ${product.price}/{product.unit}
                                  </Text>
                                  <Text fontSize="sm" color="gray.600">
                                    Stock: {product.quantity} {product.unit}s
                                  </Text>
                                  {product.brand && (
                                    <Text fontSize="xs" color="purple.600">
                                      Brand: {product.brand}
                                    </Text>
                                  )}
                                </Box>
                                <HStack spacing={1}>
                                  <IconButton
                                    aria-label="Edit item"
                                    icon={<EditIcon />}
                                    size="sm"
                                    colorScheme="purple"
                                    onClick={() => handleEditProduct(product)}
                                  />
                                  <Button
                                    size="xs"
                                    colorScheme="orange"
                                    onClick={() => {
                                      if (product._id) handleDeactivateProduct(product._id);
                                    }}
                                  >
                                    Deactivate
                                  </Button>
                                  <IconButton
                                    aria-label="Delete item"
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    colorScheme="red"
                                    onClick={() => {
                                      if (product._id) handleDeleteProduct(product._id);
                                    }}
                                  />
                                </HStack>
                              </Flex>
                            </Box>
                          ))}
                        {myProducts.filter((p) => p.status !== 'deactivated').length === 0 && (
                          <Text color="gray.600">No active products listed.</Text>
                        )}
                      </Stack>
                    </TabPanel>

                    <TabPanel px={0} py={2}>
                      <Stack spacing={4}>
                        {myProducts
                          .filter((p) => p.status === 'deactivated')
                          .map((product) => (
                            <Box key={product._id || product.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                              <Flex justify="space-between" align="start">
                                <Box>
                                  <Heading size="sm" color="gray.600">
                                    {product.name}
                                  </Heading>
                                  <Text fontSize="sm" color="gray.500">
                                    Price: ${product.price}/{product.unit}
                                  </Text>
                                </Box>
                                <IconButton
                                  aria-label="Delete item"
                                  icon={<DeleteIcon />}
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => {
                                    if (product._id) handleDeleteProduct(product._id);
                                  }}
                                />
                              </Flex>
                            </Box>
                          ))}
                        {myProducts.filter((p) => p.status === 'deactivated').length === 0 && (
                          <Text color="gray.600">No deactivated products.</Text>
                        )}
                      </Stack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Edit Item Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Item: {editingProduct?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {editingProduct?.imageUrl && (
                <Box w="full">
                  <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">
                    Current Image
                  </Text>
                  <Image
                    src={resolveProductImageUrl(editingProduct.imageUrl)}
                    alt={editingProduct.name}
                    h="120px"
                    w="full"
                    objectFit="cover"
                    borderRadius="md"
                  />
                </Box>
              )}

              <FormControl isRequired>
                <FormLabel>Item Name</FormLabel>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                >
                  <option value="seeds">Seeds & Seedlings</option>
                  <option value="fertilizers">Fertilizers & Soil Care</option>
                  <option value="pesticides">Pesticides & Chemicals</option>
                  <option value="generators">Generators & Power</option>
                  <option value="pumps">Water Pumps & Irrigation</option>
                  <option value="irrigation">Irrigation Systems</option>
                  <option value="machinery">Heavy Machinery & Tractors</option>
                  <option value="farm_tools">Farm Tools & Implements</option>
                  <option value="animal_feeds">Animal Feeds & Supplements</option>
                  <option value="veterinary">Veterinary Supplies</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Brand / Manufacturer</FormLabel>
                <Input
                  value={editForm.brand}
                  onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Condition</FormLabel>
                <Select
                  value={editForm.condition}
                  onChange={(e) => setEditForm({ ...editForm, condition: e.target.value })}
                >
                  <option value="new">Brand New</option>
                  <option value="used">Used / Pre-owned</option>
                  <option value="refurbished">Refurbished</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Price (per {editForm.unit})</FormLabel>
                <Input
                  type="number"
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Quantity</FormLabel>
                <Input
                  type="number"
                  value={editForm.quantity}
                  onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Update Image</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files) {
                      setEditImageFiles(Array.from(e.target.files));
                    }
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" mr={3} onClick={handleSaveEdit}>
              Save Changes
            </Button>
            <Button variant="ghost" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AgroDealerDashboard;
